import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  PencilSquareIcon,
  TrashIcon,
  UserGroupIcon,
  FolderIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { useClient, useClientCSATHistory, useDeleteClient } from '../../hooks/useClients';
import { useProjectsByClient } from '../../hooks/useProjects';
import { contactService } from '../../services/contact.service';
import type { ClientStatus, ProjectStatus, Contact } from '../../types';

function StatusBadge({ status }: { status: ClientStatus }) {
  const styles: Record<ClientStatus, string> = {
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-gray-100 text-gray-800',
    churned: 'bg-red-100 text-red-800',
    archived: 'bg-gray-100 text-gray-800',
  };

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${styles[status]}`}>
      {status}
    </span>
  );
}

function ProjectStatusBadge({ status }: { status: ProjectStatus }) {
  const styles: Record<ProjectStatus, string> = {
    planning: 'bg-blue-100 text-blue-800',
    active: 'bg-green-100 text-green-800',
    on_hold: 'bg-yellow-100 text-yellow-800',
    completed: 'bg-gray-100 text-gray-800',
    cancelled: 'bg-red-100 text-red-800',
    paused: 'bg-orange-100 text-orange-800',
  };

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${styles[status]}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

function CSATScoreDisplay({ score }: { score: number | null | undefined }) {
  if (score === null || score === undefined) return <span className="text-gray-400">No data</span>;

  let colorClass = 'text-red-600';
  if (score >= 80) colorClass = 'text-green-600';
  else if (score >= 60) colorClass = 'text-yellow-600';

  return <span className={`text-3xl font-bold ${colorClass}`}>{Number(score).toFixed(1)}%</span>;
}

export default function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: client, isLoading, isError } = useClient(id!);
  const { data: projects } = useProjectsByClient(id!);
  const { data: csatHistory } = useClientCSATHistory(id!);
  const { data: contacts } = useQuery({
    queryKey: ['contacts', 'client', id],
    queryFn: () => contactService.getByClient(id!),
    enabled: !!id,
  });
  const deleteClient = useDeleteClient();

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this client? This action cannot be undone.')) return;

    try {
      await deleteClient.mutateAsync(id);
      navigate('/clients');
    } catch {
      // Error handled by React Query
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  if (isError || !client) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load client details.</p>
        <Link to="/clients" className="mt-4 inline-block text-indigo-600 hover:text-indigo-900">
          ← Back to clients
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/clients"
            className="rounded-md p-1 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
              <StatusBadge status={client.status} />
            </div>
            {client.industry && (
              <p className="mt-1 text-sm text-gray-500">{client.industry}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/clients/${id}/edit`}
            className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            <PencilSquareIcon className="-ml-0.5 h-4 w-4" />
            Edit
          </Link>
          <button
            onClick={handleDelete}
            disabled={deleteClient.isPending}
            className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-red-300 hover:bg-red-50 disabled:opacity-50"
          >
            <TrashIcon className="-ml-0.5 h-4 w-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* CSAT Score Card */}
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
            <ChartBarIcon className="h-5 w-5" />
            Current CSAT Score
          </div>
          <div className="mt-3">
            <CSATScoreDisplay score={client.current_csat_score} />
            <span className="ml-1 text-sm text-gray-500">/ 5</span>
          </div>
        </div>

        {/* Projects Count */}
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
            <FolderIcon className="h-5 w-5" />
            Projects
          </div>
          <div className="mt-3">
            <span className="text-3xl font-bold text-gray-900">
              {projects?.length ?? client.projects_count ?? 0}
            </span>
          </div>
        </div>

        {/* Contacts Count */}
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
            <UserGroupIcon className="h-5 w-5" />
            Contacts
          </div>
          <div className="mt-3">
            <span className="text-3xl font-bold text-gray-900">
              {contacts?.length ?? client.contacts_count ?? 0}
            </span>
          </div>
        </div>
      </div>

      {/* Notes */}
      {client.notes && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-sm font-medium text-gray-500">Notes</h2>
          <p className="mt-2 text-sm text-gray-900 whitespace-pre-wrap">{client.notes}</p>
        </div>
      )}

      {/* Contacts Section */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">Contacts</h2>
            <Link
              to={`/contacts/new?client_id=${id}`}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-900"
            >
              + Add Contact
            </Link>
          </div>
        </div>
        <div className="divide-y divide-gray-200">
          {!contacts || contacts.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-gray-500">
              No contacts found for this client.
            </div>
          ) : (
            contacts.map((contact: Contact) => (
              <div key={contact.id} className="flex items-center justify-between px-6 py-4">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900">
                      {contact.first_name} {contact.last_name}
                    </p>
                    {contact.is_primary && (
                      <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800">
                        Primary
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{contact.email}</p>
                  {contact.position && (
                    <p className="text-xs text-gray-400">{contact.position}</p>
                  )}
                </div>
                <Link
                  to={`/contacts/${contact.id}/edit`}
                  className="text-sm text-indigo-600 hover:text-indigo-900"
                >
                  Edit
                </Link>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Projects Section */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">Projects</h2>
            <Link
              to={`/projects/new?client_id=${id}`}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-900"
            >
              + Add Project
            </Link>
          </div>
        </div>
        <div className="divide-y divide-gray-200">
          {!projects || projects.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-gray-500">
              No projects found for this client.
            </div>
          ) : (
            projects.map((project) => (
              <div key={project.id} className="flex items-center justify-between px-6 py-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/projects/${project.id}`}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-900"
                    >
                      {project.name}
                    </Link>
                    <ProjectStatusBadge status={project.status} />
                  </div>
                  {project.description && (
                    <p className="mt-1 text-sm text-gray-500 line-clamp-1">{project.description}</p>
                  )}
                </div>
                <div className="text-right">
                  {project.current_csat_score != null && (
                    <span className="text-sm font-medium text-gray-700">
                      CSAT: {Number(project.current_csat_score).toFixed(1)}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Survey History (CSAT Timeline) */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-medium text-gray-900">Survey History</h2>
        </div>
        <div className="p-6">
          {!csatHistory || csatHistory.length === 0 ? (
            <div className="text-center text-sm text-gray-500">
              No survey history available for this client.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      CSAT Score
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Trend
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {csatHistory.map((entry, index) => {
                    const prevScore = index < csatHistory.length - 1 ? csatHistory[index + 1]?.score : null;
                    const trend = prevScore !== null && prevScore !== undefined && entry.score != null 
                      ? entry.score - prevScore 
                      : null;

                    return (
                      <tr key={entry.date}>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                          {new Date(entry.date).toLocaleDateString()}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span
                            className={`text-sm font-medium ${
                              (entry.score ?? 0) >= 4
                                ? 'text-green-600'
                                : (entry.score ?? 0) >= 3
                                ? 'text-yellow-600'
                                : 'text-red-600'
                            }`}
                          >
                            {Number(entry.score ?? 0).toFixed(1)}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm">
                          {trend === null ? (
                            <span className="text-gray-400">—</span>
                          ) : trend > 0 ? (
                            <span className="text-green-600">↑ +{trend.toFixed(1)}</span>
                          ) : trend < 0 ? (
                            <span className="text-red-600">↓ {trend.toFixed(1)}</span>
                          ) : (
                            <span className="text-gray-500">→ 0.0</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Metadata */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-6 py-4">
        <div className="flex items-center gap-6 text-xs text-gray-500">
          <span>Created: {new Date(client.created_at).toLocaleDateString()}</span>
          <span>Updated: {new Date(client.updated_at).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
}
